import type {
  ConvertedItemTransition,
  ConvertedTarget,
} from "../types";

/**
 * 内部 UUID で構築された target の id / parentId / params 内 ID 参照を、
 * spec-id ベースの合成 displayId (`<type>:<specId>` / `<parent>/item:<no>`) に一括変換する。
 *
 * 入力前提: Service / View / Component の specId は target-builder で skip 済みのため非空・一意。
 */
export class IdTranslator {
  translate(targets: ConvertedTarget[]): ConvertedTarget[] {
    const idMap = this.buildIdMap(targets);
    return targets.map((t) => this.rewriteTarget(t, idMap));
  }

  private buildIdMap(targets: ConvertedTarget[]): Map<string, string> {
    const idMap = new Map<string, string>();

    for (const t of targets) {
      if (t.type !== "service" && t.type !== "view" && t.type !== "component") continue;
      const specId = t.specSource.specId;
      if (!specId) throw new Error(`IdTranslator: ${t.type} "${t.name}" specId 欠落 (target-builder の保証違反)`);
      idMap.set(t.id, `${t.type}:${escapeIdPart(specId)}`);
    }

    // item の parent が item の case (nest) はユーザーが子→親順で書き得るため
    // fixed-point loop で並び順非依存に解決。component-ref も同様に itemNo から displayId を合成。
    let pending = targets.filter(
      (t) => t.type === "item" || t.type === "section" || t.type === "overlay" || t.type === "component-ref",
    );
    while (pending.length > 0) {
      const next: typeof pending = [];
      for (const t of pending) {
        const parentDisplayId = t.parentId ? idMap.get(t.parentId) : undefined;
        if (parentDisplayId === undefined) {
          next.push(t);
          continue;
        }
        if (!t.specSource.itemNo) throw new Error(`IdTranslator: ${t.type} "${t.name}" itemNo 欠落`);
        idMap.set(t.id, `${parentDisplayId}/item:${escapeIdPart(t.specSource.itemNo)}`);
      }
      if (next.length === pending.length) {
        // 進展ゼロ = 親が一度も解決できない (target-builder の保証違反)
        const names = next.map((t) => `${t.type} "${t.name}"`).join(", ");
        throw new Error(`IdTranslator: parent displayId 未解決 [${names}]`);
      }
      pending = next;
    }

    return idMap;
  }

  private rewriteTarget(t: ConvertedTarget, idMap: Map<string, string>): ConvertedTarget {
    const newId = idMap.get(t.id) ?? t.id;
    const newParentId = t.parentId ? idMap.get(t.parentId) ?? t.parentId : undefined;
    return {
      ...t,
      id: newId,
      parentId: newParentId,
      params: rewriteParams(t, idMap),
    } as ConvertedTarget;
  }
}

function rewriteParams(t: ConvertedTarget, idMap: Map<string, string>) {
  if (t.type === "section" || t.type === "overlay" || t.type === "item") {
    const p = t.params;
    if (!p.transitions || p.transitions.length === 0) return p;
    const newTransitions: ConvertedItemTransition[] = p.transitions.map((tr) => ({
      ...tr,
      targetId: idMap.get(tr.targetId) ?? tr.targetId,
    }));
    return { ...p, transitions: newTransitions };
  }
  if (t.type === "component-ref") {
    const p = t.params;
    return {
      ...p,
      referenceTargetId: idMap.get(p.referenceTargetId) ?? p.referenceTargetId,
    };
  }
  return t.params;
}

/** displayId の予約文字 (`\` `:` `/`) を `\` で escape。任意の specId / itemNo を含められる。 */
export function escapeIdPart(s: string): string {
  return s.replace(/[\\:/]/g, "\\$&");
}
